import { memo } from 'react'

import { Audiotrack, BrokenImageOutlined, VideocamOffOutlined } from '@mui/icons-material'
import { Box, styled } from '@mui/material'

import { StreamKind } from 'domains/Workspaces/Streams'

/**
 * The `Thumbnail` in `domains/Workspaces/Events/layouts` is hardcoded to 64x36 for table cells.
 * A detail-page hero needs a larger one, and adding a size prop there would touch every
 * cross-domain importer of it.
 */
const Frame = styled('object')(({ theme }) => ({
  width: 160,
  height: 90,
  aspectRatio: '16 / 9',
  objectFit: 'contain',
  borderRadius: theme.shape.borderRadius / 2,
  display: 'grid',
  placeItems: 'center',
  backgroundColor: theme.palette.background.levels[500],
}))

const Placeholder = styled(Box)(({ theme }) => ({
  width: 160,
  height: 90,
  borderRadius: theme.shape.borderRadius / 2,
  display: 'grid',
  placeItems: 'center',
  backgroundColor: theme.palette.background.levels[500],
  '& > svg': {
    width: 'auto',
    height: 40,
  },
}))

export type StreamThumbnailProps = {
  streamKind: StreamKind
  url?: string | null
  alt?: string
}

const StreamThumbnailRaw = ({ streamKind, url, alt }: StreamThumbnailProps) => {
  // Anything that isn't a loadable http(s) url (e.g. an unresolved internal `nw://`) gets the placeholder.
  const src = url && /^https?:\/\//i.test(url) ? url : undefined

  const FallbackIcon = streamKind === 'audio' ? Audiotrack : VideocamOffOutlined

  return (
    <Box sx={{ flexShrink: 0 }}>
      {src ? (
        <picture>
          <Frame type="image/jpeg" data={src} aria-label={alt}>
            <Placeholder>
              <BrokenImageOutlined />
            </Placeholder>
          </Frame>
        </picture>
      ) : (
        <Placeholder>
          <FallbackIcon />
        </Placeholder>
      )}
    </Box>
  )
}

export const StreamThumbnail = memo(StreamThumbnailRaw)
